import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { autorun } from 'mobx';
import { isEqual, isEmpty } from 'lodash';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { Grid, Fab, Paper } from '@material-ui/core';
import { Done as DoneIcon } from '@material-ui/icons';
import { useToggle } from 'react-use';
import {
  AppBar,
  FormDialog,
  ConfirmationDialog,
  Page,
  LoanBorrower,
  LoanBorrowerSkeleton,
  OtherCategories,
  OtherCategoriesSkeleton,
  ScoreInfo,
  ScoreInfoSkeleton,
} from 'components';
import { ru } from 'locale';
import { decisionStore, anketsStore, userStore, contragentStore } from 'stores';
import { diff } from 'utils/objects';
import { getDateTimePickerFormat } from 'utils/date';
import { removeNthUrlSegment } from 'utils/string';
import EditForm from './components/EditForm';
import DecisionItem from './components/DecisionItem';
import DecisionItemSkeleton from './components/DecisionItem/DecisionItemSkeleton';
import {
  modalEditedFieldsPipeline,
  permissionAccessCategories,
  categoriesUpBlock,
  categoriesDownBlock,
} from './DecisionScreen.mock';
import useStyles from './DecisionScreen.styles';

const DecisionScreen = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const { decisionId } = useParams();
  const { anketId } = anketsStore;
  const { fetchState, decision } = decisionStore;
  const { userData, fetchState: userDataState } = userStore;
  const { contragentInfo, fetchState: contragentDataState } = contragentStore;
  const [activeFieldName, setFieldName] = useState('');
  const [fieldValues, setFieldValues] = useState({});
  const [initialEditedFieldValues, setInitialEditedFieldValues] = useState({});
  const [isFormDialogOpen, toggleFormDialog] = useToggle(false);
  const [isConfirmationDialogOpen, toggleConfirmationDialog] = useToggle(false);
  const isOwner = userData?.login === decision?.login;
  const { url } = useRouteMatch();
  const parentUrl = removeNthUrlSegment(url, 1);

  useEffect(() => {
    decisionStore.getDecision(anketId, decisionId);
  }, [anketId, decisionId]);

  useEffect(
    () =>
      autorun(() => {
        contragentStore.getContragentInfo(anketsStore.anketId);
      }),
    [],
  );

  useEffect(() => {
    if (fetchState === 'fulfilled') {
      setFieldValues(decision);
    }
  }, [decision, fetchState]);

  useEffect(() => {
    if (contragentDataState === 'fulfilled' && contragentInfo?.initiator && fieldValues) {
      const limitForEdit =
        fieldValues.limit === 0 ? contragentInfo.initiator.value : fieldValues.limit;
      const termForEdit = fieldValues.term === 0 ? contragentInfo.initiator.term : fieldValues.term;
      setInitialEditedFieldValues({ ...fieldValues, limit: limitForEdit, term: termForEdit });
    }
  }, [contragentDataState, contragentInfo, fieldValues]);

  const handleBackClick = () => {
    if (!isEqual(decision, fieldValues)) {
      toggleConfirmationDialog();
    } else {
      history.push(parentUrl);
    }
  };

  const handleFormDialogOpen = name => {
    if (isOwner) {
      setFieldName(name);
      toggleFormDialog();
    }
  };

  const openNextDialogByPipeline = name => {
    const indexCurrentModal = modalEditedFieldsPipeline.indexOf(name);
    if (modalEditedFieldsPipeline.length > indexCurrentModal + 1) {
      handleFormDialogOpen(modalEditedFieldsPipeline[indexCurrentModal + 1]);
    }
  };

  const handleSubmitForm = data => {
    const formattedDate = getDateTimePickerFormat(data.dateTo, 'yyyy-MM-d');
    const newData = { ...data, dateTo: formattedDate };
    setFieldValues(newData);
    toggleFormDialog();
    openNextDialogByPipeline(activeFieldName);
  };

  const handleDecisionReset = () => {
    toggleConfirmationDialog();
    history.goBack();
  };

  const handleDecisionUpdate = () => {
    if (!isEqual(decision, fieldValues)) {
      const touchedFields = diff(fieldValues, decision);
      decisionStore.updateDecision(anketId, decisionId, touchedFields);
    }
    history.goBack();
  };

  const renderDecision = () => {
    return (
      <Paper>
        <DecisionItem decision={fieldValues} onEdit={handleFormDialogOpen} />
        <FormDialog
          id="form-dialog"
          open={isFormDialogOpen}
          initialValues={initialEditedFieldValues}
          onClose={toggleFormDialog}
          onSubmit={handleSubmitForm}
          noPadding={activeFieldName === 'dateTo'}
        >
          {formikBag => <EditForm formikBag={formikBag} fieldName={activeFieldName} />}
        </FormDialog>
      </Paper>
    );
  };

  return (
    <Page>
      <Grid container className={classes.pageContainer}>
        <Grid item container direction="column" xs={12} className={classes.pageItem}>
          <Grid item>
            <AppBar title={ru.decision} onNavButtonClick={handleBackClick} />
          </Grid>
          {fetchState === 'pending' && <DecisionItemSkeleton />}
          <Grid item>
            {fetchState === 'fulfilled' && !isEmpty(fieldValues) && renderDecision()}
          </Grid>
        </Grid>

        <Grid item container direction="column" xs={12} className={classes.pageItem}>
          <Grid item className={classes.loanBorrowerWrapper}>
            {contragentDataState === 'pending' && <LoanBorrowerSkeleton />}
            {contragentDataState === 'fulfilled' && (
              <LoanBorrower borrower={contragentInfo.initiator} screen="decision" />
            )}
          </Grid>
        </Grid>

        <Grid item container direction="column" xs={12} className={classes.pageItem}>
          <Grid item>
            {userDataState === 'pending' && <OtherCategoriesSkeleton screen="decision" />}
            {userDataState === 'fulfilled' && (
              <OtherCategories
                role={userData.role}
                categories={categoriesUpBlock}
                permissionAccessCategories={permissionAccessCategories}
              />
            )}
          </Grid>
        </Grid>

        <Grid item container direction="column" xs={12} className={classes.pageItem}>
          <Grid item>
            {contragentDataState === 'pending' && <ScoreInfoSkeleton />}
            {contragentDataState === 'fulfilled' && <ScoreInfo score={contragentInfo.score} />}
          </Grid>
        </Grid>

        <Grid item container direction="column" xs={12} className={classes.pageItem}>
          <Grid item>
            {userDataState === 'pending' && <OtherCategoriesSkeleton screen="decision" />}
            {userDataState === 'fulfilled' && (
              <OtherCategories
                role={userData.role}
                categories={categoriesDownBlock}
                permissionAccessCategories={permissionAccessCategories}
              />
            )}
          </Grid>
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        title={ru.changedDecision}
        description={ru.unsavedChanges}
        onClose={handleDecisionReset}
        onSubmit={handleDecisionUpdate}
      />

      <Fab
        color="secondary"
        aria-label={ru.save}
        className={classes.saveButton}
        onClick={handleDecisionUpdate}
      >
        <DoneIcon />
      </Fab>
    </Page>
  );
});

export default DecisionScreen;
